import { useState } from 'react';

import { Checkbox } from '../components/Checkbox.js';
import { Icon } from '../components/Icon.js';
import { plural } from '../util/format.js';
import { searchJobTree, selectableIds, type JobLeaf, type JobTreeNode } from './job-tree.js';
import styles from './JobTreeList.module.css';

interface JobTreeListProps {
  tree: JobTreeNode[];
  /** Lowercased and trimmed by the caller, which owns the box; empty is no cut. */
  needle: string;
  selected: readonly string[];
  onToggle: (id: string) => void;
  /** Take or put down everything the search is showing, in one press. */
  onSelectMany: (ids: string[], on: boolean) => void;
  /** Said when nothing here is bound to a posting at all. */
  emptyNote: string;
}

/**
 * THE POSTING TREE, DRAWN ONCE. `/chat`'s filter dialog and the widget's scope
 * drawer both render this, so a rule about what a parent is cannot hold on one
 * surface and not the other.
 */
export function JobTreeList({
  tree,
  needle,
  selected,
  onToggle,
  onSelectMany,
  emptyNote,
}: JobTreeListProps) {
  /**
   * Families the employer has opened or shut by hand. Per-visit, so it rides on
   * this list's own mount: a family left open is not something to remember about
   * someone.
   */
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const isSelected = (id: string) => selected.includes(id);
  const { nodes: visible, opened } = searchJobTree(tree, needle, isSelected);
  const selectable = selectableIds(visible);
  const allTaken = selectable.length > 0 && selectable.every(isSelected);

  // One selectable job — a standalone posting, a sub-role, or (when its family
  // is expanded) the parent job's own self-row. Every checkbox is exactly one.
  const renderLeaf = (leaf: JobLeaf) => (
    <label className={styles.tickRow} title={leaf.title}>
      <Checkbox checked={isSelected(leaf.id)} onChange={() => onToggle(leaf.id)} />
      <span className={styles.jobText}>
        <span className={styles.tickText}>
          <span className={styles.tickLabel}>{leaf.title}</span>
          {leaf.count > 0 && <span className={styles.count}>({leaf.count})</span>}
        </span>
        {leaf.location && <span className={styles.jobLocation}>{leaf.location}</span>}
      </span>
    </label>
  );

  const renderNode = (node: JobTreeNode) => {
    if (node.kind !== 'parent') {
      return <li key={node.parent.id}>{renderLeaf(node.parent)}</li>;
    }

    // The parent is the card's lid, not a checkbox. Its own box would mean its
    // own applicants, never its children's — so one beside a family total of 24
    // would claim 24 and pick 5, and where the parent takes no applicants of
    // its own it would be dead. A family tick is buildable, but only as a
    // tri-state roll-up, which contradicts `buildJobTree`'s rule that every
    // checkbox is exactly one job. That wants deciding on its own.
    //
    // Open on a pick or a search hit inside — one the employer cannot see is
    // narrowing the list with nothing on screen to explain why. A press still
    // wins, or the disclosure is dead in exactly the cases it is needed.
    const anySelected =
      isSelected(node.parent.id) || node.children.some((child) => isSelected(child.id));
    const isOpen = toggled[node.parent.id] ?? (anySelected || opened.has(node.parent.id));
    // What the card holds, and so what opening it shows: every sub-role, plus
    // the parent's own posting where it takes applicants of its own.
    const inside = node.children.length + (node.parent.selectable ? 1 : 0);

    return (
      <li key={node.parent.id} className={styles.group}>
        <button
          type="button"
          className={styles.parentHeader}
          aria-expanded={isOpen}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.parent.title}`}
          onClick={() => setToggled((current) => ({ ...current, [node.parent.id]: !isOpen }))}
        >
          <span className={styles.parentText}>
            <span className={styles.parentTitle}>{node.parent.title}</span>
            {/* Counts the rows the card opens to, so the number is a promise the
                disclosure keeps.

                "Similar" is the product call, made against the argument for a
                bare count and kept. The argument: an employer's list carries
                near-identical titles that are separate postings and stay
                ungrouped — three Product Managers, two Digital Marketing
                Specialists — so naming similarity over the one family that *is*
                grouped invites the question of why the others are not, the real
                rule being AJT's parent/child posting tree rather than the
                titles. Answered: "similar" is what the grouping looks like from
                outside, and the employer reading this row is reading the card,
                not the tree. Recorded so it is found already weighed. */}
            <span className={styles.parentSub}>{plural(inside, 'similar job posting')}</span>
          </span>
          {/* Open as well as shut. Expanding used to take the total away at the
              one moment it is worth having — with the parts on screen under it,
              (9) over (6) and (3) is the sum the employer is checking. */}
          {node.familyCount > 0 && <span className={styles.count}>({node.familyCount})</span>}
          <span className={styles.headerChevron}>
            <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={15} />
          </span>
        </button>
        {isOpen && (
          <ul className={styles.childList}>
            {node.parent.selectable && <li>{renderLeaf(node.parent)}</li>}
            {node.children.map((child) => (
              <li key={child.id}>{renderLeaf(child)}</li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  if (tree.length === 0) return <p className={styles.empty}>{emptyNote}</p>;

  return (
    <>
      {/* The count is the promise: under a search it names the rows on screen. */}
      {selectable.length > 0 && (
        <label className={styles.selectAll}>
          <Checkbox checked={allTaken} onChange={() => onSelectMany(selectable, !allTaken)} />
          {allTaken ? 'Unselect all' : 'Select all'} {selectable.length}
        </label>
      )}
      {visible.length === 0 ? (
        <p className={styles.empty}>{`No job matches “${needle}”.`}</p>
      ) : (
        <ul className={styles.tickList}>{visible.map(renderNode)}</ul>
      )}
    </>
  );
}
